import { UserPreferences, MapPreferences, Theme, MarkerStyle } from "../types";
import debugx from "debugx";
import { wakeLockService } from "./wakeLockService";

const debug = debugx("bikedash:settings");

export interface Settings {
  user: UserPreferences;
  map: MapPreferences;
}

const DEFAULT_USER_PREFERENCES: UserPreferences = {
  // Display
  units: "imperial",
  theme: "auto",
  activityTheme: "dark",
  activityBarLayout: "expanded",
  textSize: "medium",
  keepScreenOn: true,

  // Privacy
  defaultActivityPrivacy: "friends",
  shareLocationWithFriends: false,
  proximityAlertsEnabled: true,
  proximityRadius: 5000, // 5km

  // Activity
  autoSaveInterval: 30, // seconds
  inactivityTimeout: 5, // minutes (0 = disabled, 1-30)
  inactivityDistance: 2, // km (0 = disabled)
  voiceAnnouncements: false,
  announcementVolume: 70,

  // Data & Sync
  offlineMode: false,
  autoSync: true,
  syncOverCellular: false,
  offlineMapRadius: 10, // km
  apiKeys: {},

  // Performance
  maxUpdateRate: 30, // Max FPS for all animation updates (1-60fps)

  // Simulation
  resumeSimulationOnReload: false, // Default to false
};

const DEFAULT_MAP_PREFERENCES: MapPreferences = {
  defaultStyleId: "esri:satellite",
  starredStyleIds: ["esri:satellite", "osm:standard", "thunderforest:cycle"],
  satelliteUnderlay: false,
  overlayOpacity: 0,
  markerStyle: "arrow",
  markerColor: "#3e6ae1",
  autoFollow: true,
  smoothMotion: true,
  useGpsHeading: false, // Default to compass for compatibility
  activityTrack: {
    showLine: true,
    lineWidth: 8,
    lineColor: "#3e6ae1",
    lineOpacity: 0.8,
    showOutline: true,
    outlineColor: "auto",
    showPoints: false,
    pointsInterval: 10,
    showDirection: true,
    gradientBySpeed: false,
    fadeOldSegments: true,
  },
  routeTrack: {
    showLine: true,
    lineWidth: 4,
    lineColor: "#22c55e",
    lineOpacity: 0.8,
    showOutline: true,
    outlineColor: "auto",
  },
  overlaySettings: {
    showStarredPlaces: true,
    showFriends: true,
    showCompass: false,
    showScale: true,
    showDebugInfo: false,
  },
};

class SettingsService {
  private settings: Settings;
  private callbacks: Set<(settings: Settings) => void> = new Set();

  constructor() {
    this.settings = this.loadSettings();
    // Apply settings on initial load
    this.applySettings();
  }

  private loadSettings(): Settings {
    // Load from localStorage first
    try {
      const stored = localStorage.getItem("bikedash_settings");
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all fields exist
        return {
          user: { ...DEFAULT_USER_PREFERENCES, ...parsed.user },
          map: { ...DEFAULT_MAP_PREFERENCES, ...parsed.map },
        };
      }
    } catch (error) {
      debug.error("Failed to load settings:", error);
    }

    // Return defaults
    return {
      user: DEFAULT_USER_PREFERENCES,
      map: DEFAULT_MAP_PREFERENCES,
    };
  }

  private saveSettings() {
    try {
      localStorage.setItem("bikedash_settings", JSON.stringify(this.settings));
      debug("Settings saved to localStorage:", this.settings);
    } catch (error) {
      debug.error("Failed to save settings:", error);
    }

    // Don't sync to server immediately - this might be causing issues
    // this.syncToServer();
  }

  private async syncToServer() {
    if (this.settings.user.offlineMode) return;

    try {
      // Check if we're authenticated
      const cachedAuth = localStorage.getItem("bikedash_cached_auth");
      if (!cachedAuth) return;

      const response = await fetch("/api/settings", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(this.settings),
      });

      if (response.ok) {
        debug("Settings synced to server");
      }
    } catch (error) {
      debug.warn("Failed to sync settings to server (will retry):", error);
    }
  }

  async loadFromServer() {
    try {
      const response = await fetch("/api/settings", {
        method: "GET",
        credentials: "include",
      });

      if (response.ok) {
        const serverSettings = await response.json();
        // Only update if we got actual settings back
        if (serverSettings && (serverSettings.user || serverSettings.map)) {
          // Merge server settings with local (server wins)
          this.settings = {
            user: { ...this.settings.user, ...serverSettings.user },
            map: { ...this.settings.map, ...serverSettings.map },
          };
          this.saveSettings();
          this.notifySubscribers();
          debug("Settings loaded from server");
        }
      }
    } catch (error) {
      debug.warn("Failed to load settings from server:", error);
    }
  }

  // ============================================================================
  // Public API
  // ============================================================================

  getSettings(): Settings {
    return this.settings;
  }

  getUserPreferences(): UserPreferences {
    return this.settings.user;
  }

  getMapPreferences(): MapPreferences {
    return this.settings.map;
  }

  // Update entire user preferences
  updateUserPreferences(preferences: Partial<UserPreferences>) {
    this.settings.user = { ...this.settings.user, ...preferences };
    this.saveSettings();
    this.notifySubscribers();
    this.applySettings();
  }

  // Update entire map preferences
  updateMapPreferences(preferences: Partial<MapPreferences>) {
    debug("Updating map preferences:", preferences);
    this.settings.map = { ...this.settings.map, ...preferences };
    this.saveSettings();
    this.notifySubscribers();
  }

  // Convenience methods for common settings
  setTheme(theme: Theme) {
    this.updateUserPreferences({ theme });
  }

  setUnits(units: "metric" | "imperial") {
    this.updateUserPreferences({ units });
  }

  setKeepScreenOn(keepOn: boolean) {
    this.updateUserPreferences({ keepScreenOn: keepOn });
  }

  setDefaultMapStyle(styleId: string) {
    this.updateMapPreferences({ defaultStyleId: styleId });
  }

  toggleStarredMap(styleId: string) {
    const current = this.settings.map.starredStyleIds;
    const isStarred = current.includes(styleId);

    if (isStarred) {
      // Don't allow unstarring if it's the only one
      if (current.length === 1) return;
      this.updateMapPreferences({
        starredStyleIds: current.filter((id) => id !== styleId),
      });
    } else {
      this.updateMapPreferences({
        starredStyleIds: [...current, styleId],
      });
    }
  }

  setApiKey(service: string, key: string) {
    const apiKeys = { ...this.settings.user.apiKeys };

    if (key) {
      // Set the key if not empty
      apiKeys[service] = key;
    } else {
      // Remove the key if empty
      delete apiKeys[service];
    }

    this.updateUserPreferences({ apiKeys });
  }

  // Apply settings to the app
  private applySettings() {
    const { theme, keepScreenOn } = this.settings.user;

    // Apply theme
    this.applyTheme(theme);

    // Apply wake lock
    if (keepScreenOn) {
      wakeLockService.enable();
    } else {
      wakeLockService.disable();
    }
  }

  private applyTheme(theme: Theme) {
    const root = document.documentElement;

    // Remove all theme classes
    root.classList.remove(
      "theme-light",
      "theme-dark",
      "theme-high-contrast-light",
      "theme-high-contrast-dark",
    );

    if (theme === "auto") {
      // Use system preference
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      root.classList.add(prefersDark ? "theme-dark" : "theme-light");
    } else {
      root.classList.add(`theme-${theme}`);
    }
  }

  // Subscribe to settings changes
  subscribe(callback: (settings: Settings) => void): () => void {
    this.callbacks.add(callback);
    return () => {
      this.callbacks.delete(callback);
    };
  }

  private notifySubscribers() {
    this.callbacks.forEach((callback) => callback(this.settings));
  }
}

export const settingsService = new SettingsService();
